import { assignProps } from '@nzyme/utils/assignProps.js';
import { defineProps } from '@nzyme/vue-utils/defineProps.js';
import { useProps } from '@nzyme/vue-utils/useProps.js';
import { h, nextTick, onMounted, ref, watch } from 'vue';
import type { HTMLAttributes } from 'vue';

import { defineFormField } from './defineFormField.js';

const TEXT_AREA_FIELD = defineFormField<string | null | undefined>(String);
const TEXT_AREA_PROPS = defineProps({
    ...TEXT_AREA_FIELD.props,
    label: String,
    name: String,
    placeholder: String,
    tabindex: Number,
});

/** Auto-resizing textarea composable providing a form field and its render component. */
export const useTextArea = assignProps(setupTextArea, {
    props: TEXT_AREA_PROPS,
    emits: TEXT_AREA_FIELD.emits,
});

/**
 * @__NO_SIDE_EFFECTS__
 */
function setupTextArea() {
    const props = useProps(TEXT_AREA_PROPS);
    const field = TEXT_AREA_FIELD.create({ props });
    const input = ref<HTMLTextAreaElement>();

    onMounted(updateValue);
    watch(() => field.value, updateValue);

    return {
        field,
        input,
        TextArea,
    };

    async function updateValue() {
        if (!input.value) {
            return;
        }

        input.value.value = field.value || '';
        await updateHeight();
    }

    async function updateHeight() {
        await nextTick();

        if (!input.value) {
            return;
        }

        input.value.style.height = '0';
        input.value.style.height = input.value.scrollHeight + 'px';
    }

    function onInput(event: Event) {
        const target = event.target as HTMLTextAreaElement;
        field.value = target.value;
        void updateHeight();
    }

    function TextArea(attrs: HTMLAttributes) {
        return (
            <textarea
                {...attrs}
                aria-label={props.label}
                aria-readonly={props.readonly}
                aria-required={props.required}
                disabled={props.disabled}
                name={props.name}
                onBlur={field.inputAttrs.onBlur}
                onFocus={field.inputAttrs.onFocus}
                onInput={onInput}
                placeholder={props.placeholder}
                readonly={props.readonly}
                ref={input}
                rows={1}
                tabindex={props.tabindex}
                title={props.label}
            />
        );
    }
}
